import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Landmark, Search } from "lucide-react";
import { inr, fmtDate } from "@/utils";
import { toast } from "sonner";
import { usePagination } from "@/components/Pagination";

export default function LandOwners() {
  const qc = useQueryClient();
  const { data: projects = [] } = useQuery({ queryKey: ["projects"], queryFn: async () => (await api.get("/projects")).data });
  const jdaProjects = projects.filter((p) => p.ownership_type === "jda");

  const { data: owners = [] } = useQuery({
    queryKey: ["land-owners"],
    queryFn: async () => (await api.get("/land-owners")).data,
  });

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({
    project_id: "", name: "", address: "", pan: "", aadhaar: "", gst: "",
    phone: "", email: "", agreement_date: "", agreement_expiry: "",
    land_area_sqft: 0, revenue_share_model: "percentage",
    share_percent: 0, fixed_amount: 0, unit_allocation_count: 0, notes: "",
  });

  async function save() {
    if (!form.project_id || !form.name) return toast.error("Project and name required");
    try {
      await api.post("/land-owners", {
        ...form,
        land_area_sqft: +form.land_area_sqft || 0,
        share_percent: +form.share_percent || 0,
        fixed_amount: +form.fixed_amount || 0,
        unit_allocation_count: +form.unit_allocation_count || 0,
      });
      toast.success("Land owner recorded");
      qc.invalidateQueries({ queryKey: ["land-owners"] });
      setOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  const projectMap = Object.fromEntries(projects.map((p) => [p.id, p]));

  const filteredOwners = owners.filter((o) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (o.name || "").toLowerCase().includes(q) ||
      (o.phone || "").toLowerCase().includes(q) ||
      (o.pan || "").toLowerCase().includes(q) ||
      (o.email || "").toLowerCase().includes(q) ||
      (projectMap[o.project_id]?.name || "").toLowerCase().includes(q)
    );
  });

  const { pageRows: pagedOwners, controls: ownerControls } = usePagination(filteredOwners, 10);

  return (
    <div className="space-y-6" data-testid="land-owners-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-emerald-700">Joint Development</div>
          <h1 className="font-heading text-3xl font-bold mt-1">Land Owners (JDA)</h1>
          <p className="text-sm text-zinc-500 mt-1">Track landowners, revenue sharing models (%, fixed, units, hybrid), and their payment obligations.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
              <Input
                placeholder="Search owner name, phone, PAN…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-9 w-64 pl-8"
                data-testid="owner-search"
              />
            </div>
            <DialogTrigger asChild><Button className="bg-emerald-700 hover:bg-emerald-800" data-testid="add-owner-btn"><Plus size={14} className="mr-1" /> Add Land Owner</Button></DialogTrigger>
          </div>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle className="font-heading">Add Land Owner</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Project (JDA)</Label>
                <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Select JDA project" /></SelectTrigger>
                  <SelectContent>
                    {jdaProjects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    {!jdaProjects.length && <SelectItem value=" " disabled>No JDA projects yet</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Full Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="owner-name" /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label>PAN</Label><Input value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value })} /></div>
              <div><Label>Aadhaar</Label><Input value={form.aadhaar} onChange={(e) => setForm({ ...form, aadhaar: e.target.value })} /></div>
              <div><Label>GST</Label><Input value={form.gst} onChange={(e) => setForm({ ...form, gst: e.target.value })} /></div>
              <div className="col-span-2"><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
              <div><Label>Agreement Date</Label><Input type="date" value={form.agreement_date} onChange={(e) => setForm({ ...form, agreement_date: e.target.value })} /></div>
              <div><Label>Agreement Expiry</Label><Input type="date" value={form.agreement_expiry} onChange={(e) => setForm({ ...form, agreement_expiry: e.target.value })} /></div>
              <div><Label>Land Area (sqft)</Label><Input type="number" value={form.land_area_sqft} onChange={(e) => setForm({ ...form, land_area_sqft: e.target.value })} /></div>
              <div><Label>Sharing Model</Label>
                <Select value={form.revenue_share_model} onValueChange={(v) => setForm({ ...form, revenue_share_model: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage</SelectItem>
                    <SelectItem value="fixed">Fixed Amount</SelectItem>
                    <SelectItem value="units">Unit Allocation</SelectItem>
                    <SelectItem value="hybrid">Hybrid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Share %</Label><Input type="number" value={form.share_percent} onChange={(e) => setForm({ ...form, share_percent: e.target.value })} /></div>
              <div><Label>Fixed Amount (₹)</Label><Input type="number" value={form.fixed_amount} onChange={(e) => setForm({ ...form, fixed_amount: e.target.value })} /></div>
              <div><Label>Units Allocated</Label><Input type="number" value={form.unit_allocation_count} onChange={(e) => setForm({ ...form, unit_allocation_count: e.target.value })} /></div>
              <div className="col-span-2"><Label>Notes</Label><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
            <DialogFooter><Button onClick={save} className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-owner">Save land owner</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {pagedOwners.map((o) => (
          <Card key={o.id} className="bg-white border-zinc-200" data-testid={`owner-card-${o.id}`}>
            <CardContent className="p-5 space-y-3">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <Landmark size={16} className="text-amber-600" />
                    <div className="font-heading text-lg font-semibold">{o.name}</div>
                  </div>
                  <div className="text-xs text-zinc-500 mt-1">{projectMap[o.project_id]?.name || "—"}</div>
                </div>
                <span className="text-[10px] uppercase tracking-widest bg-amber-100 text-amber-800 border border-amber-300 px-2 py-0.5 rounded">{o.revenue_share_model}</span>
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div><div className="text-[10px] uppercase tracking-widest text-zinc-500">PAN</div><div className="font-mono">{o.pan || "—"}</div></div>
                <div><div className="text-[10px] uppercase tracking-widest text-zinc-500">GST</div><div className="font-mono">{o.gst || "—"}</div></div>
                <div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Agreement</div><div className="font-mono">{fmtDate(o.agreement_date)} → {fmtDate(o.agreement_expiry)}</div></div>
                <div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Land Area</div><div className="font-mono">{o.land_area_sqft} sqft</div></div>
              </div>
              <div className="border-t border-zinc-200 pt-3 grid grid-cols-3 gap-2 text-xs">
                {o.share_percent > 0 && <div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Share %</div><div className="font-mono font-semibold">{o.share_percent}%</div></div>}
                {o.fixed_amount > 0 && <div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Fixed</div><div className="font-mono font-semibold">{inr(o.fixed_amount)}</div></div>}
                {o.unit_allocation_count > 0 && <div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Units</div><div className="font-mono font-semibold">{o.unit_allocation_count}</div></div>}
              </div>
              {o.notes && <p className="text-xs text-zinc-600 border-t border-zinc-200 pt-3">{o.notes}</p>}
            </CardContent>
          </Card>
        ))}
        {!owners.length && (
          <Card className="col-span-full border-dashed border-zinc-300 bg-white">
            <CardContent className="p-16 text-center text-sm text-zinc-500">
              No land owners yet. Add one to your first <b>JDA project</b>.
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
